import React, { useEffect, useState } from 'react';
import { useMountedState } from 'react-use';

import BuoyMapMarker from 'components/BuoyMapMarker';
import ClusterMarker from 'components/ClusterMarker';
import ErrorBoundary from 'components/ErrorBoundary';
import { Pane } from 'components/Map';
import useMap from 'hooks/map/useMap';
import type { NearbyStation } from 'types/buoys';
import type { Cluster } from 'utils/cluster/cluster';

import styles from './BuoyMapMarkers.module.scss';

interface Props {
  clusters?: Cluster<{ count: number }>[] | null;
  buoys?: NearbyStation[];
}

const BuoyMapMarkers: React.FC<Props> = ({ buoys = [], clusters }) => {
  const [map] = useMap();
  const isMounted = useMountedState();
  const [mapReady, setMapReady] = useState(false);

  // Watch for map changes and ensure we re-render when a new map instance is available
  useEffect(() => {
    if (map?.getContainer?.()) {
      // Add a small delay to ensure the map container is fully ready
      const timer = setTimeout(() => {
        if (isMounted()) {
          setMapReady(true);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    setMapReady(false);
    return undefined;
  }, [map, isMounted]);

  // If component is unmounted or map is not ready, don't render to prevent state updates
  if (!isMounted() || !map || !mapReady) {
    return null;
  }

  return (
    <Pane
      name="sl-buoy-map-markers"
      className={styles.slBuoyMapMarkers}
      usingPortal
      map={map}
      style={{}}
    >
      {clusters && clusters?.length > 0
        ? clusters.map((cluster) => (
            <ErrorBoundary
              errorBoundaryId="buoy-map-markers-cluster"
              key={`${cluster.geometry.coordinates[0]}-${cluster.geometry.coordinates[1]}`}
              render={() => null}
            >
              <ClusterMarker cluster={cluster} />
            </ErrorBoundary>
          ))
        : buoys.map((buoy) => (
            // Don't render anything if the marker throws
            <ErrorBoundary errorBoundaryId="buoy-map-markers" key={buoy.id} render={() => null}>
              <BuoyMapMarker buoy={buoy} />
            </ErrorBoundary>
          ))}
    </Pane>
  );
};

export default BuoyMapMarkers;
